<h2>{{ "CASE_FORM.FORMS.SUMMARY.TITLE" | translate }}</h2>
<section
  class="summary-data top-margin-20"
  data-testid="summary-form"
>
  <!-- Client -->
  <div
    class="flex vcenter"
    data-testid="summary-client"
  >
    <div class="grow1 summary-label">
      {{ "CASE_FORM.FORMS.SUMMARY.CLIENT_LABEL" | translate }}
    </div>
    <div
      class="summary-value"
      data-testid="summary-client-name"
    >
      {{ summary.client.name }}
    </div>
  </div>
  <!-- Debtor -->
  <div
    class="flex vcenter"
    data-testid="summary-debtor"
  >
    <div class="grow1 summary-label">
      {{ "CASE_FORM.FORMS.SUMMARY.DEBTOR_LABEL" | translate }}
    </div>
    <div
      class="summary-value"
      data-testid="summary-debtor-name"
    >
      {{ summary.debtor.name }}
    </div>
  </div>
  <!-- Claims -->
  <div>
    <div class="flex">
      <div class="grow1 summary-label">
        {{ "CASE_FORM.FORMS.SUMMARY.CLAIMS_LABEL" | translate }}
      </div>
      <div>
        @for (sum of summary.payee_case.displayed_claims_sums | keyvalue; track sum.key) {
          <div class="summary-amount">
            {{ sum.value.remaining_amount | number: "1.0" : "hu" }}
            {{ sum.key }}
          </div>
        }
      </div>
    </div>
    <div class="top-margin-5">
      @for (claim of summary.claims; track claim.id; let i = $index) {
        <div class="flex vcenter">
          <div class="grow1 claim-item-label">
            {{ i + 1 }}. {{ "CASE_FORM.FORMS.SUMMARY.CLAIM_ITEM_LABEL" | translate }} ({{
              "CASE_FORM.FORMS.SUMMARY.CLAIM_DUE_LABEL" | translate
            }}
            {{ claim.due_date_at | date: "yyyy.MM.dd." }})
          </div>
          <div class="claim-item-amount">
            {{ claim.original_amount | number: "1.0" : "hu" }}
            {{ claim.currency_iso }}
          </div>
        </div>
      }
    </div>
  </div>
  <!-- Optional Claims -->
  @if (showOptionalClaimsForm) {
    <div class="optional-claims">
      <div class="optional-claim-title flex vcenter">
        <payee-icon
          name="plus-black"
          width="15"
          height="15"
        ></payee-icon>
        &nbsp;{{ "CASE_FORM.FORMS.SUMMARY.OPTIONAL_CLAIMS_TITLE" | translate }}
      </div>
      @if (showInterestAddedForm) {
        <payee-toggle
          matTooltip="{{ 'CASE_FORM.FORMS.SUMMARY.INTEREST_TOOLTIP' | translate }}"
          matTooltipPosition="above"
          matTooltipClass="custom-tooltip"
          direction="top"
          [disabled]="loading()"
          [selected]="isInterestAdded.value"
          [text]="
            !interestAmount
              ? ('CASE_FORM.FORMS.SUMMARY.INTEREST_OPTIONAL_CLAIM_LABEL_UNKNOWN' | translate)
              : ('CASE_FORM.FORMS.SUMMARY.INTEREST_OPTIONAL_CLAIM_LABEL' | translate: { amount: interestAmount })
          "
          (selectionChanged)="onInterestToggle($event)"
        ></payee-toggle>
      }
      @if (showFlatRateCostAddedForm) {
        <payee-toggle
          matTooltip="{{ 'CASE_FORM.FORMS.SUMMARY.FLAT_RATE_TOOLTIP' | translate }}"
          matTooltipPosition="above"
          matTooltipClass="custom-tooltip"
          [selected]="isFlatRateCostClaimAdded.value"
          [disabled]="loading()"
          [text]="
            !flatRateCostAmount
              ? ('CASE_FORM.FORMS.SUMMARY.FLAT_RATE_OPTIONAL_CLAIM_LABEL_UNKNOWN' | translate)
              : ('CASE_FORM.FORMS.SUMMARY.FLAT_RATE_OPTIONAL_CLAIM_LABEL'
                | translate
                  : {
                      amount: flatRateCostAmount,
                      currency: startFeesCurrencyIso,
                    })
          "
          (selectionChanged)="onFlatRateCostClaimToggle($event)"
        ></payee-toggle>
      }
      @if (isStartCasePriceClaimAdded.value) {
        <payee-toggle
          matTooltip="{{ 'CASE_FORM.FORMS.SUMMARY.START_CASE_TOOLTIP' | translate }}"
          matTooltipPosition="above"
          matTooltipClass="custom-tooltip"
          [selected]="true"
          [disabled]="true"
          [text]="
            !startCaseFeeAmount
              ? ('CASE_FORM.FORMS.SUMMARY.START_CASE_FEE_OPTIONAL_CLAIM_LABEL_UNKNOWN' | translate)
              : ('CASE_FORM.FORMS.SUMMARY.START_CASE_FEE_OPTIONAL_CLAIM_LABEL'
                | translate
                  : {
                      start_fee_amount: startCaseFeeAmount | number: '1.0' : 'hu',
                      currency: startFeesCurrencyIso,
                    })
          "
        ></payee-toggle>
      }
    </div>
  }
</section>

<h2 class="top-margin-30">
  {{ "CASE_FORM.FORMS.SUMMARY.ACTION_AND_PRICING_TITLE" | translate }}
</h2>
@if (showProductForms) {
  <div class="payment-method-description">
    <span>
      {{ "CASE_FORM.FORMS.SUMMARY.HELP_TEXT" | translate }}
    </span>
    <a
      href="https://help.payee.tech/hu/articles/6423716-melyik-modult-valasszam"
      target="_blank"
    >
      {{ "CASE_FORM.FORMS.SUMMARY.MORE_INFO" | translate }}
    </a>
  </div>
}
<mat-radio-group
  class="product-type-selector flex column alt-theme top-margin-20"
  color="primary"
  [formControl]="productType"
>
  @if (shouldShowProduct("notice")) {
    <mat-radio-button
      value="notice"
      (click)="selectProductType('notice', $event)"
    >
      <div class="product-type-title">
        {{ "CASE_FORM.FORMS.SUMMARY.NOTICE_TITLE" | translate }}
      </div>
      <div class="product-type-description">
        {{ "CASE_FORM.FORMS.SUMMARY.NOTICE_DESCRIPTION" | translate }}
      </div>
    </mat-radio-button>
  }
  @if (shouldShowProduct("demand_letter")) {
    <mat-radio-button
      value="demand_letter"
      (click)="selectProductType('demand_letter', $event)"
    >
      <div class="product-type-title">
        {{ "CASE_FORM.FORMS.SUMMARY.DEMAND_LETTER_TITLE" | translate }}
      </div>
      <div class="product-type-description">
        {{ "CASE_FORM.FORMS.SUMMARY.DEMAND_LETTER_DESCRIPTION" | translate }}
      </div>
    </mat-radio-button>
  }
  @if (shouldShowProduct("hard_1")) {
    <mat-radio-button
      value="hard_1"
      (click)="selectProductType('hard_1', $event)"
    >
      <div class="flex vcenter hard-radio-wrapper">
        <div>
          <div class="product-type-title">
            {{ "CASE_FORM.FORMS.SUMMARY.HARD_1_TITLE" | translate }}
          </div>
          <div class="product-type-description">
            {{ "CASE_FORM.FORMS.SUMMARY.HARD_1_DESCRIPTION" | translate }}
          </div>
        </div>
        <a
          matTooltip="{{
            'CASE_FORM.FORMS.SUMMARY.HARD_1_TOOLTIP'
              | translate
                : {
                    hard_2_price: summary.product.hard_2_price.amount | number: '1.0' : 'hu',
                    hard_2_min_price: summary.product.hard_2_price.min | number: '1.0' : 'hu',
                    hard_2_max_price: summary.product.hard_2_price.max | number: '1.0' : 'hu',
                  }
          }}"
          href="https://help.payee.tech/hu/articles/6423716-melyik-modult-valasszam"
          target="_blank"
          class="flex vcenter hard-recommended-box alt-theme"
          matTooltipClass="custom-tooltip"
          matTooltipPosition="above"
          [matTooltipDisabled]="
            summary.product.hard_2_price.amount === null ||
            summary.product.hard_2_price.min === null ||
            summary.product.hard_2_price.max === null
          "
          (click)="$event.stopPropagation()"
        >
          <payee-icon name="info-grey-outline"></payee-icon>
          <span class="recommended-chip">
            {{ "CASE_FORM.FORMS.SUMMARY.RECOMMENDED_LABEL" | translate }}
          </span>
        </a>
      </div>
    </mat-radio-button>
  }
  @if (shouldShowProduct("coface")) {
    <mat-radio-button
      value="coface"
      (click)="selectProductType('coface', $event)"
    >
      <div class="flex vcenter hard-radio-wrapper">
        <div class="title-box">
          <div class="product-type-title">
            {{ "CASE_FORM.FORMS.SUMMARY.COFACE_TITLE" | translate }}
          </div>
          <div class="product-type-description">
            {{ "CASE_FORM.FORMS.SUMMARY.COFACE_DESCRIPTION" | translate }}
          </div>
        </div>
        <a
          matTooltip="{{ 'CASE_FORM.FORMS.SUMMARY.COFACE_TOOLTIP' | translate }}"
          href="https://www.coface.hu/koeveteleskezeles"
          target="_blank"
          class="flex vcenter hard-recommended-box alt-theme"
          matTooltipClass="custom-tooltip"
          matTooltipPosition="above"
          (click)="$event.stopPropagation()"
        >
          <payee-icon name="info-grey-outline"></payee-icon>
          <img
            alt=""
            width="66px"
            height="auto"
            src="assets/shared/global-images/partner-logos/coface.svg"
          />
        </a>
      </div>
    </mat-radio-button>
  }
</mat-radio-group>

@if (!isSelectedProductAvailable) {
  <div
    class="product-unavailable-banner top-margin-20"
    role="alert"
  >
    <mat-icon>error_outline</mat-icon>
    <div class="product-unavailable-content">
      <div class="product-unavailable-title">
        {{ summary.product_unavailable.title }}
      </div>
      <div class="product-unavailable-description">
        {{ summary.product_unavailable.description }}
      </div>
      @if (selectedProductUnavailableReasons.length > 0) {
        <ul class="product-unavailable-reasons">
          @for (reason of selectedProductUnavailableReasons; track reason) {
            <li>{{ reason }}</li>
          }
        </ul>
      }
    </div>
  </div>
}

<!-- Fees -->
<div class="fees top-margin-20">
  @if ((startCaseFeeAmount ?? 0) > 0) {
    <div class="flex vcenter bottom-margin-5">
      <div class="grow1 start-fee-title">
        {{ "CASE_FORM.FORMS.SUMMARY.START_CASE_FEE_LABEL" | translate }}
      </div>
      <div class="start-fee-title">
        {{ startCaseFeeAmount | number: "1.0" : "hu" }}
        {{ startFeesCurrencyIso }}
      </div>
    </div>
  }
  @if (successCaseFeeAmount > 0) {
    <div class="flex vcenter bottom-margin-5">
      <div class="grow1 start-fee-title">
        {{ "CASE_FORM.FORMS.SUMMARY.SUCCESS_FEE_LABEL" | translate }}
      </div>
      <div class="start-fee-title">
        {{ successCaseFeeAmount | number: "1.0" : "hu" }}
        {{ successFeesCurrencyIso }}
      </div>
    </div>
  }
  @for (fees of summary.fees | keyvalue; track fees.key) {
    @for (fee of fees.value; track fee.label) {
      <div class="flex vcenter">
        <div class="grow1 fee-text">
          {{ fee.label }}
        </div>
        <span class="fee-detail">{{ fee.details_text }}&nbsp;</span>
        <span class="fee-amount">
          @if (fee.amount !== null) {
            {{ fee.amount | number: "1.0" : "hu" }}
            {{ fee.currency_iso }}
          } @else {
            ?
          }
        </span>
      </div>
    }
  }
  <!-- All Claims -->
  @if (showAllClaimsAmount) {
    <div class="all-claims top-margin-10">
      <div class="flex">
        <div class="all-claims-label grow1">
          {{ "CASE_FORM.FORMS.SUMMARY.ALL_CLAIMS_LABEL" | translate }}
        </div>
        <div>
          @for (sum of summary.payee_case.claims_sums | keyvalue; track sum.key) {
            <div class="all-claims-amount">
              {{ sum.value.remaining_amount | number: "1.0" : "hu" }}
              {{ sum.key }}
            </div>
          }
        </div>
      </div>
      <div class="all-claims-description">
        {{ "CASE_FORM.FORMS.SUMMARY.ALL_CLAIMS_DESCRIPTION" | translate: { name: summary.debtor.name } }}
      </div>
    </div>
  }
</div>

@if (showCofaceForm) {
  <div class="top-margin-20">
    <app-coface-form></app-coface-form>
  </div>
}

@if (showDebtorForm) {
  <div class="top-margin-20">
    <app-notice-form></app-notice-form>
  </div>
}

<!-- Payment Method -->
@if (isSelectedProductAvailable && (startCaseFeeAmount ?? 0) > 0) {
  <h2 class="top-margin-30">
    {{ "CASE_FORM.FORMS.SUMMARY.PAYMENT_METHOD_TITLE" | translate }}
  </h2>
  <mat-radio-group
    class="payment-method-selector flex column alt-theme top-margin-20"
    color="primary"
    [formControl]="paymentMethod"
  >
    <mat-radio-button
      value="card"
      data-testid="payment-method-card"
    >
      <div class="flex vcenter">
        <div class="flex column grow1">
          <div class="payment-method-title">
            {{ "CASE_FORM.FORMS.SUMMARY.CARD_PAYMENT_TITLE" | translate }}
          </div>
          <div class="payment-method-description">
            {{ "CASE_FORM.FORMS.SUMMARY.CARD_PAYMENT_DESCRIPTION" | translate }}
          </div>
        </div>
        <img
          src="assets/shared/global-images/partner-logos/barion.svg"
          width="151px"
          alt="barion"
        />
      </div>
    </mat-radio-button>
    <mat-radio-button
      value="transfer"
      data-testid="payment-method-transfer"
    >
      <div class="payment-method-title">
        {{ "CASE_FORM.FORMS.SUMMARY.TRANSFER_PAYMENT_TITLE" | translate }}
      </div>
      <div class="payment-method-description">
        {{ "CASE_FORM.FORMS.SUMMARY.TRANSFER_PAYMENT_DESCRIPTION" | translate }}
      </div>
    </mat-radio-button>
  </mat-radio-group>
  <button
    type="button"
    class="summary-cta-button top-margin-25"
    data-testid="summary-cta"
    [disabled]="loading() || hasUnsavedChanges"
    (click)="startCaseWithPayment()"
  >
    <span class="summary-cta-glow"></span>
    @if (paymentMethod.value === "card") {
      <span class="summary-cta-text">
        {{ startCaseFeeAmount | number: "1.0" : "hu" }}
        {{ startFeesCurrencyIso }}
        {{ "CASE_FORM.FORMS.SUMMARY.PAY_CARD_LABEL" | translate }}
      </span>
    }
    @if (paymentMethod.value === "transfer") {
      <span class="summary-cta-text">
        {{
          "CASE_FORM.FORMS.SUMMARY.PAY_TRANSFER_LABEL"
            | translate
              : {
                  amount: startCaseFeeAmount | number: "1.0" : "hu",
                  currency: startFeesCurrencyIso,
                }
        }}
      </span>
    }
  </button>
  <div class="payment-disclaimer top-margin-20">
    {{ "CASE_FORM.FORMS.SUMMARY.PAYMENT_DISCLAIMER" | translate }}
  </div>
}
@if (startCaseFeeAmount === 0) {
  <button
    type="button"
    class="summary-cta-button top-margin-25"
    data-testid="summary-cta"
    [disabled]="loading() || hasUnsavedChanges || !isSelectedProductAvailable"
    (click)="startInstantly()"
  >
    <span class="summary-cta-glow"></span>
    @if (loading()) {
      <mat-progress-spinner
        class="summary-cta-spinner"
        diameter="28"
        mode="indeterminate"
        color="accent"
      ></mat-progress-spinner>
    }
    @if (!loading()) {
      <span class="summary-cta-text">{{ "CASE_FORM.FORMS.SUMMARY.START_NOW_BUTTON" | translate }}</span>
    }
  </button>
  <div class="payment-disclaimer top-margin-20">
    {{ "CASE_FORM.FORMS.SUMMARY.INSTANT_PAYMENT_DISCLAIMER" | translate }}
  </div>
}
@if (startCaseFeeAmount === null) {
  <button
    type="button"
    class="summary-cta-button top-margin-25"
    data-testid="summary-cta"
    [disabled]="loading() || hasUnsavedChanges || !isSelectedProductAvailable"
    (click)="startWithUnknownPayment()"
  >
    <span class="summary-cta-glow"></span>
    <span class="summary-cta-text">{{ "CASE_FORM.FORMS.SUMMARY.UNKNOWN_TRANSFER_LABEL" | translate }}</span>
  </button>
}
